import { PROGRAM_REGISTRY, type ProgramCode, type ProgramRegistryEntry } from '@/config/programs';
import type { ReviewableResource } from '../types/resource';

/**
 * Prefixes the backend uses for the per-program review-access role.
 * The canonical form on staging today is `RESOUCE_REVIEW.<code>` —
 * the missing `R` is a backend typo we're forced to match against
 * the wire, but we also accept the corrected spelling for the day
 * the backend fixes it, so no client change will be needed then.
 */
const REVIEW_ROLE_PREFIXES = ['RESOUCE_REVIEW.', 'RESOURCE_REVIEW.'] as const;

/**
 * True when the roles array grants review access to any of the given
 * program codes. `ADMIN` always wins; otherwise the user must hold
 * one of the `RESOUCE_REVIEW.<code>` roles for at least one code the
 * program groups. Sports's 5-code group means any one of
 * `RESOUCE_REVIEW.SD` / `.RGSH` / `.RGSF` / `.RGST` / `.RGSG` grants
 * access.
 *
 * Roles are compared by exact match (not prefix) — the backend emits
 * one role per program-code grant, not per sub-role.
 */
export function hasReviewAccessForCodes(
  codes: readonly string[],
  roles: string[] | undefined,
): boolean {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  if (roles.includes('ADMIN')) return true;
  return codes.some((code) =>
    REVIEW_ROLE_PREFIXES.some((prefix) => roles.includes(`${prefix}${code}`)),
  );
}

/**
 * True when the roles array grants access to this program. Thin
 * wrapper over `hasReviewAccessForCodes` — kept as the entry point
 * for callers that already have a `ProgramRegistryEntry`, so they
 * don't have to unpack `.codes` themselves.
 */
export function hasProgramAccess(
  program: ProgramRegistryEntry,
  roles: string[] | undefined,
): boolean {
  return hasReviewAccessForCodes(program.codes, roles);
}

/**
 * Filter the resources returned by `/console/review/resources` down
 * to the ones that belong to a specific program.
 *
 * Attribution follows this priority:
 *
 *   1. Explicit `program` field on the resource (backend-authoritative
 *      — not currently emitted, but reserved for when the API adds it).
 *   2. Resource-name prefix (`rf_*` → RF, `ll_*` → LL, `sd_*` → Sports,
 *      etc.). Handles the naming convention the majority of resource
 *      tables already follow.
 *   3. **Safe fallback**: resources that can't be attributed either way
 *      are surfaced under *every* program the user has access to. The
 *      server has already role-filtered `/resources` for the caller,
 *      so a resource in this bucket is by definition something the
 *      user is allowed to review — the only ambiguity is *which*
 *      program's sidebar entry should surface it. Rather than hide it
 *      arbitrarily under one program, we surface it under every
 *      program the user is browsing so it's always discoverable.
 *
 * Once the backend adds a `program` field per resource, branch 3 will
 * see zero traffic and can be removed.
 */
export function filterResourcesForProgram(
  resources: ReviewableResource[],
  program: ProgramRegistryEntry,
): ReviewableResource[] {
  const programCodes = new Set(program.codes.map((c) => c.toUpperCase()));
  const prefixes = program.codes.map((c) => `${c.toLowerCase()}_`);
  // Set of every program code we recognise anywhere in the app —
  // used to identify "prefix-attributable to some OTHER program"
  // and skip those from the fallback bucket.
  const knownPrefixes = KNOWN_PROGRAM_PREFIXES;

  return resources.filter((r) => {
    const explicitProgram = typeof r.program === 'string' && r.program.length > 0
      ? r.program.toUpperCase()
      : undefined;

    // (1) Explicit program field is authoritative in both directions —
    // when set, it decides on its own regardless of the name prefix.
    if (explicitProgram !== undefined) {
      return programCodes.has(explicitProgram);
    }

    const title = r.title?.toLowerCase() ?? '';

    // (2) Name-prefix matches this program.
    if (prefixes.some((prefix) => title.startsWith(prefix))) {
      return true;
    }

    // (3) Safe fallback: no explicit program AND name doesn't prefix
    // any known program code → surface under every program. This
    // catches the pilot's `kissan_sruvey` / `outreach_survey` case
    // where the backend hasn't wired attribution yet.
    const attributedElsewhere = knownPrefixes.some((p) => title.startsWith(p));
    return !attributedElsewhere;
  });
}

/**
 * Inverse lookup — given a resource, which program does it belong to?
 * Used for breadcrumbs when a route deep-links to a resource without
 * the program slug in the URL. Returns `undefined` when the resource
 * can't be attributed.
 */
export function programCodeForResource(
  resource: Pick<ReviewableResource, 'title' | 'program'>,
): ProgramCode | undefined {
  if (typeof resource.program === 'string') {
    return resource.program.toUpperCase() as ProgramCode;
  }
  const title = resource.title?.toLowerCase() ?? '';
  const prefixMatch = title.match(/^([a-z]+)_/);
  if (!prefixMatch) return undefined;
  return prefixMatch[1].toUpperCase() as ProgramCode;
}

/**
 * Every program prefix we know about, lower-cased with the trailing
 * `_`. Derived from `PROGRAM_REGISTRY.codes` so a new program added
 * to the registry automatically joins this set.
 */
const KNOWN_PROGRAM_PREFIXES: string[] = PROGRAM_REGISTRY
  .flatMap((p) => p.codes)
  .map((c) => `${c.toLowerCase()}_`);
